import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Tenant Context
 *
 * Extracts tenant from subdomain for multi-tenant routing
 * URL format: {tenant}.bcportal.app/{company}/{page}
 * Example: goodstuff365.bcportal.app/Cronus/dashboard
 *
 * Tenant = Customer organization (identified by subdomain)
 * Company = BC company within that tenant's environment
 */

interface Tenant {
  subdomain: string;
  name: string | null;
  id: string | null;
}

interface TenantContextValue {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
  subdomain: string | undefined;
  updateTenantDetails: (details: Partial<Tenant>) => void;
  getTenantHeader: () => Record<string, string>;
  isTenantLoaded: boolean;
}

interface TenantProviderProps {
  children: React.ReactNode;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export const useTenant = (): TenantContextValue => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

/**
 * Extract tenant subdomain from current hostname
 * Supports:
 * - {tenant}.bcportal.app -> tenant
 * - {tenant}.localhost -> tenant (local dev with subdomain)
 * - localhost -> 'goodstuff365' (default for local dev)
 * - {tenant}.{project}.pages.dev -> tenant (Cloudflare Pages preview)
 * - ?tenant=xxx query param -> tenant (for preview testing)
 */
const extractTenantSubdomain = (): string | null => {
  const hostname = window.location.hostname;

  // Production: goodstuff365.bcportal.app - subdomain is authoritative
  // Do NOT allow query param override on production for security
  if (hostname.endsWith('.bcportal.app')) {
    return hostname.replace('.bcportal.app', '');
  }

  // For non-production (preview, localhost): allow ?tenant= query param
  const urlParams = new URLSearchParams(window.location.search);
  const tenantParam = urlParams.get('tenant');
  if (tenantParam) {
    // Store in sessionStorage so it persists during navigation
    sessionStorage.setItem('preview_tenant', tenantParam);
    return tenantParam;
  }

  // Check sessionStorage for previously set preview tenant
  const previewTenant = sessionStorage.getItem('preview_tenant');
  if (previewTenant && hostname.endsWith('.pages.dev')) {
    return previewTenant;
  }

  // Local dev with subdomain: goodstuff365.localhost
  if (hostname.endsWith('.localhost')) {
    return hostname.replace('.localhost', '');
  }

  // Cloudflare Pages preview: may have tenant prefix
  if (hostname.endsWith('.pages.dev')) {
    const parts = hostname.split('.');
    // Pattern: {tenant}.{project}.pages.dev (where tenant is not a commit hash)
    // Commit hashes are 8 hex chars, tenant names are usually not
    if (parts.length >= 3 && parts[0] !== 'bcportal-db' && !/^[0-9a-f]{8}$/i.test(parts[0])) {
      return parts[0];
    }
    // Fallback for Pages preview without tenant - use default
    return 'goodstuff365';
  }

  // Plain localhost or 127.0.0.1 - use default tenant for development
  if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
    // Check for override in localStorage (for dev testing)
    const devTenant = localStorage.getItem('dev_tenant_override');
    return devTenant || 'goodstuff365';
  }

  return null;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenant = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const subdomain = extractTenantSubdomain();

        if (!subdomain) {
          setError('Could not determine tenant from URL');
          setLoading(false);
          return;
        }

        // Set basic tenant info from subdomain
        // Full tenant details will be fetched after auth
        setTenant({
          subdomain,
          name: null, // Will be populated from API
          id: null,   // Will be populated from API
        });

        // Tag Sentry errors with tenant for filtering
        if (import.meta.env.VITE_SENTRY_DSN) {
          Sentry.setTag('tenant', subdomain);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading tenant:', err);
        setError((err as Error).message || 'Failed to load tenant');
        setLoading(false);
      }
    };

    loadTenant();
  }, []);

  /**
   * Update tenant with full details from API response
   */
  const updateTenantDetails = (details: Partial<Tenant>): void => {
    setTenant(prev => ({
      ...prev,
      ...details,
    } as Tenant));
  };

  /**
   * Get header for API requests in development mode
   */
  const getTenantHeader = (): Record<string, string> => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) {
      return { 'X-Tenant-Subdomain': tenant?.subdomain || 'goodstuff365' };
    }
    return {};
  };

  const value: TenantContextValue = {
    tenant,
    loading,
    error,
    subdomain: tenant?.subdomain,
    updateTenantDetails,
    getTenantHeader,
    isTenantLoaded: !loading && !error && tenant !== null,
  };

  // Show error state for invalid tenant
  if (error && !tenant) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Tenant Not Found</h2>
        <p style={{ color: '#d32f2f' }}>{error}</p>
        <p style={{ marginTop: '1rem', color: '#666' }}>
          Please check the URL and ensure you&apos;re using the correct subdomain.
        </p>
        <p style={{ fontSize: '0.9em', color: '#999' }}>
          Example: <code>goodstuff365.bcportal.app/Cronus/login</code>
        </p>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
